import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Space,
  Typography,
  message,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { PmsAPI } from "../../../controller/PmsAPI";

export default function ChannelCommissionPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    PmsAPI.commission
      .index()
      .then((res) => setList(res.data || []))
      .catch(() => message.error("載入佣金設定失敗"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ commission_rate: 0, deduct: false, sort: 10, active: true });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({ ...row, commission_rate: +(row.commission_rate * 100).toFixed(2) });
    setModalOpen(true);
  };

  const submit = () => {
    form.validateFields().then((v) => {
      const payload = { ...v, commission_rate: (v.commission_rate || 0) / 100, deduct: !!v.deduct, active: !!v.active };
      const req = editing
        ? PmsAPI.commission.update(editing.id, payload)
        : PmsAPI.commission.create(payload);
      req
        .then(() => {
          message.success("已儲存");
          setModalOpen(false);
          load();
        })
        .catch(() => message.error("儲存失敗"));
    });
  };

  const remove = (row) => {
    PmsAPI.commission
      .remove(row.id)
      .then(() => {
        message.success("已刪除");
        load();
      })
      .catch(() => message.error("刪除失敗"));
  };

  const columns = [
    { title: "平台", dataIndex: "platform", key: "platform" },
    {
      title: "對應樣式 (CUST_NAM LIKE)",
      dataIndex: "match_pattern",
      key: "match_pattern",
      render: (v) => v || <Typography.Text type="secondary">(不自動對應)</Typography.Text>,
    },
    {
      title: "佣金",
      dataIndex: "commission_rate",
      key: "commission_rate",
      align: "right",
      render: (v) => `${+(v * 100).toFixed(2)}%`,
    },
    { title: "營收另扣佣金", dataIndex: "deduct", key: "deduct", align: "center", render: (v) => (v ? "是" : "否") },
    { title: "收款方式", dataIndex: "payment_method", key: "payment_method" },
    { title: "平台優惠", dataIndex: "member_discount", key: "member_discount" },
    { title: "排序", dataIndex: "sort", key: "sort", align: "right" },
    { title: "啟用", dataIndex: "active", key: "active", align: "center", render: (v) => (v ? "✓" : "—") },
    {
      title: "操作",
      key: "action",
      render: (t, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>編輯</Button>
          <Popconfirm title={`刪除「${row.platform}」?`} onConfirm={() => remove(row)} okText="刪除" cancelText="取消">
            <Button size="small" danger>刪除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={3} style={{ marginBottom: 8 }}>通路佣金設定</Typography.Title>
      <Typography.Paragraph type="secondary">
        佣金率用於通路統計的「淨營收 = 營收 × (1 − 佣金)」。對應樣式以 SQL LIKE 比對訂房來源 (ORDER_MN.CUST_NAM),
        例如 %易遊網% 可涵蓋各分公司;留空表示不自動對應 (如 Google ads)。多個樣式命中時,依「排序」小者優先。
      </Typography.Paragraph>
      {/* 操作按鈕獨立一列，緊鄰它作用的表格;標題自己一列不與控制項混排 */}
      <Space style={{ marginBottom: 12 }} align="center">
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>新增</Button>
      </Space>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={list} size="small" pagination={false} scroll={{ x: "max-content" }} />

      <Modal
        title={editing ? `編輯:${editing.platform}` : "新增通路佣金"}
        visible={modalOpen}
        onOk={submit}
        onCancel={() => setModalOpen(false)}
        okText="儲存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="platform" label="平台名稱" rules={[{ required: true, message: "請輸入平台名稱" }]}>
            <Input placeholder="例如 Booking、德安官網" />
          </Form.Item>
          <Form.Item name="match_pattern" label="對應樣式 (CUST_NAM LIKE)" extra="例如 Booking%、%易遊網%、官網訂房;留空則不自動對應">
            <Input placeholder="Booking%" />
          </Form.Item>
          <Form.Item name="commission_rate" label="佣金 (%)" rules={[{ required: true, message: "請輸入佣金" }]}>
            <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} addonAfter="%" />
          </Form.Item>
          <Form.Item
            name="deduct"
            label="營收另扣佣金"
            valuePropName="checked"
            extra="開:PMS 營收為未扣佣金的毛額 (如 Booking 現場付款),淨營收再扣;關:PMS 營收已是扣完佣金的淨額,不再扣"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="payment_method" label="收款方式">
            <Input placeholder="現場付款 / 預收款項..." />
          </Form.Item>
          <Form.Item name="member_discount" label="平台優惠">
            <Input placeholder="會員85折..." />
          </Form.Item>
          <Form.Item name="note" label="備註">
            <Input />
          </Form.Item>
          <Form.Item name="sort" label="排序 (小者優先比對)">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="active" label="啟用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
